import { store } from '@/app/store';
import { getUnsentMessages } from '@/features/chats/model/conversation/selectors';
import { upsertRecentChatItem } from '@/features/chats/model/recentChat';
import { formatRecentChat, formatUnsentMessages } from '@/features/chats/utils/messageFormatter';
import { logger } from '@/shared/utils';
import { bufferMessages, markChatLoadEnd, markChatLoadStart } from './messageBuffer';
import { queueMessage } from './messageQueue';

// chatId -> re-stamped unsent messages (newest-first). Held between capture and
// restore so performDataSync can clear/refresh Redux in between.
let captured = {};

/**
 * Snapshot every still-unsent message (sand-timer) from Redux BEFORE the sync
 * layer clears/overwrites it, re-stamp to the bottom, and park it in the
 * message buffer. markChatLoadStart holds each buffer (no 500ms auto-flush) so
 * the data survives until the user opens the chat (markChatLoadEnd flushes it)
 * — or until restoreUnsentMessages flushes the active chat directly.
 *
 * Safe to call on every performDataSync (initial login finds nothing).
 */
export const captureUnsentMessages = () => {
   try {
      captured = {};
      const conversationData = store.getState().conversationData?.data ?? {};

      for (const chatId of Object.keys(conversationData)) {
         const unsent = getUnsentMessages(chatId);
         if (!unsent.length) continue;

         const reformatted = formatUnsentMessages({ messages: unsent });
         captured[chatId] = reformatted;

         markChatLoadStart(chatId);
         bufferMessages(chatId, reformatted);
      }
   } catch (err) {
      logger.error('[unsentRetention] capture error:', err);
      captured = {};
   }
};

/**
 * After the sync completes: re-trigger delivery and surface the retained data.
 *   • text/meet  → re-queued for send (resend logic lives in the queue/SDK).
 *   • media      → retained only (no auto-resume); user retries manually.
 *   • recent list→ upserted so the unsent message shows as the last message.
 *   • active chat→ buffer flushed into the freshly-synced Redux so the open
 *                  conversation shows the retained messages immediately. Other
 *                  chats stay buffered until opened.
 */
export const restoreUnsentMessages = activeChatId => {
   try {
      for (const [chatId, messages] of Object.entries(captured)) {
         // Oldest-first so the queue sends them in composition order.
         const ordered = [...messages].reverse();
         for (const msg of ordered) {
            const type = msg.msgType ?? 'text';
            if (type === 'text' || type === 'meet') {
               queueMessage(msg, chatId, type);
            }
         }

         // messages is newest-first → [0] is the latest for the recent-chat row.
         store.dispatch(upsertRecentChatItem(formatRecentChat(messages[0], { changeArchiveStatus: true })));
      }

      if (activeChatId && captured[activeChatId]) {
         // The open chat's load effect won't re-fire on reconnect, so release
         // the hold — markChatLoadEnd flushes its buffered messages into the
         // freshly-synced store.
         markChatLoadEnd(activeChatId);
      }
   } catch (err) {
      logger.error('[unsentRetention] restore error:', err);
   } finally {
      captured = {};
   }
};
